import { createFeature, createReducer, on } from "@ngrx/store";
import { IssueTrackerActions } from "@store/actions/issue-tracker/issue-tracker.actions";
import {
  Ticket,
  TicketCategory,
  TicketStatus,
} from "@models/ticket-data.model";

export const issueTrackerFeatureKey = "issue-tracker";

export interface State {
  tickets: Ticket[];
  ticketCategories: TicketCategory[];
  ticketStatuses: TicketStatus[];
  downloadingAttachment: boolean;
  previewingAttachment: boolean;
  loading: boolean;
  categoriesLoading: boolean;
  statusesLoading: boolean;
  error: string | null;
  totalCount: number;
}

export const initialState: State = {
  tickets: [],
  ticketCategories: [],
  ticketStatuses: [],
  loading: false,
  categoriesLoading: false,
  statusesLoading: false,
  downloadingAttachment: false,
  previewingAttachment: false,
  error: null,
  totalCount: 0,
};

export const reducer = createReducer(
  initialState,

  on(IssueTrackerActions.getTickets, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(IssueTrackerActions.getTicketsSuccess, (state, { tickets }) => ({
    ...state,
    tickets: tickets.map((ticket) => ({
      ...ticket,
      ticketAttachments: ticket.ticketAttachments
        ? [...ticket.ticketAttachments].sort(
            (a, b) =>
              new Date(b.createDate).getTime() -
              new Date(a.createDate).getTime()
          )
        : [],
    })),
    totalCount: tickets.length,
    loading: false,
    error: null,
  })),
  on(IssueTrackerActions.getTicketsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(IssueTrackerActions.createTicket, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(IssueTrackerActions.createTicketSuccess, (state, { ticket }) => ({
    ...state,
    tickets: [ticket, ...state.tickets],
    totalCount: state.totalCount + 1,
    loading: false,
    error: null,
  })),
  on(IssueTrackerActions.createTicketFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(IssueTrackerActions.updateTicket, (state) => ({
    ...state,
    loading: true,
    error: null,
  })),
  on(IssueTrackerActions.updateTicketSuccess, (state, { ticket }) => ({
    ...state,
    tickets: state.tickets.map((t) => (t.id === ticket.id ? ticket : t)),
    loading: false,
    error: null,
  })),
  on(IssueTrackerActions.updateTicketFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(IssueTrackerActions.addTicketCommentSuccess, (state) => ({
    ...state,
    error: null,
  })),
  on(IssueTrackerActions.addTicketCommentFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(
    IssueTrackerActions.deleteTicketCommentSuccess,
    (state, { commentId }) => ({
      ...state,
      tickets: state.tickets.map((ticket) =>
        ticket.ticketComments?.some((c) => c.id === commentId)
          ? {
              ...ticket,
              ticketComments: ticket.ticketComments.filter(
                (c) => c.id !== commentId
              ),
            }
          : ticket
      ),
      error: null,
    })
  ),
  on(IssueTrackerActions.deleteTicketCommentFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(IssueTrackerActions.populateCommentData, (state, { comment }) => ({
    ...state,
    tickets: state.tickets.map((ticket) =>
      ticket.id === comment.ticketId
        ? {
            ...ticket,
            ticketComments: [comment, ...(ticket.ticketComments || [])],
          }
        : ticket
    ),
    error: null,
  })),
  on(IssueTrackerActions.uploadTicketAttachmentSuccess, (state) => ({
    ...state,
    error: null,
  })),
  on(IssueTrackerActions.uploadTicketAttachmentFailure, (state, { error }) => ({
    ...state,
    error,
  })),
on(IssueTrackerActions.deleteTicketAttachmentSuccess, (state, { attachmentId }) => ({
  ...state,
  tickets: state.tickets.map(ticket => 
    ticket.ticketAttachments?.some(a => a.id === attachmentId) ? {
      ...ticket,
      ticketAttachments: ticket.ticketAttachments.filter(a => a.id !== attachmentId)
    } : ticket
  ),
  error: null
})),
  on(IssueTrackerActions.deleteTicketAttachmentFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(IssueTrackerActions.updateTicketAttachmentSuccess, (state, { attachment }) => ({
    ...state,
    tickets: state.tickets.map((ticket) =>
      ticket.ticketAttachments?.some((a) => a.id === attachment.id)
        ? {
            ...ticket,
            ticketAttachments: ticket.ticketAttachments.map((a) =>
              a.id === attachment.id ? { ...a, ...attachment } : a
            ),
          }
        : ticket
    ),
    error: null,
  })),
  on(IssueTrackerActions.updateTicketAttachmentFailure, (state, { error }) => ({
    ...state,
    error,
  })),
on(IssueTrackerActions.populateAttachmentData, (state, { attachment }) => {
  const newState = {
    ...state,
    tickets: state.tickets.map((ticket) =>
      ticket.id === attachment.ticketId
        ? {
            ...ticket,
            ticketAttachments: [
              attachment,
              ...(ticket.ticketAttachments || []),
            ],
          }
        : ticket
    ),
    error: null,
  };
  return newState;
}),
  on(IssueTrackerActions.getTicketCategories, (state) => ({
    ...state,
    categoriesLoading: true,
    error: null,
  })),
  on(
    IssueTrackerActions.getTicketCategoriesSuccess,
    (state, { categories }) => ({
      ...state,
      ticketCategories: categories,
      categoriesLoading: false,
      error: null,
    })
  ),
  on(IssueTrackerActions.getTicketCategoriesFailure, (state, { error }) => ({
    ...state,
    categoriesLoading: false,
    error,
  })),
  on(IssueTrackerActions.getTicketStatuses, (state) => ({
    ...state,
    statusesLoading: true,
    error: null,
  })),
  on(IssueTrackerActions.getTicketStatusesSuccess, (state, { statuses }) => ({
    ...state,
    ticketStatuses: statuses,
    statusesLoading: false,
    error: null,
  })),
  on(IssueTrackerActions.getTicketStatusesFailure, (state, { error }) => ({
    ...state,
    statusesLoading: false,
    error,
  })),
  on(IssueTrackerActions.downloadAttachment, (state) => ({
    ...state,
    downloadingAttachment: true,
    error: null,
  })),
  on(IssueTrackerActions.downloadAttachmentSuccess, (state) => ({
    ...state,
    downloadingAttachment: false,
    error: null,
  })),
  on(IssueTrackerActions.downloadAttachmentFailure, (state, { error }) => ({
    ...state,
    downloadingAttachment: false,
    error,
  })),
  on(IssueTrackerActions.previewAttachment, (state) => ({
    ...state,
    previewingAttachment: true,
    error: null,
  })),
  on(IssueTrackerActions.previewAttachmentSuccess, (state) => ({
    ...state,
    previewingAttachment: false,
    error: null,
  })),
  on(IssueTrackerActions.previewAttachmentFailure, (state, { error }) => ({
    ...state,
    previewingAttachment: false,
    error,
  }))
);

export const issueTrackerFeature = createFeature({
  name: issueTrackerFeatureKey,
  reducer,
});
